/**
 * QuestionRenderer — US-CAP-104
 *
 * Renders the correct answer input based on question_type.
 * Used in:
 *   - SelfAssessmentPage  (readOnly=false)
 *   - ReviewAssessmentPage (readOnly=true, with optional manual score for text_box)
 *   - ConfigureQuestions   (readOnly=true, live preview)
 *
 * Props:
 *   question_type    : 'five_option' | 'yes_no' | 'checkbox' | 'radio' | 'text_box'
 *   answer_config    : object matching the type shape (or null for legacy five_option)
 *   value            : current answer value (type depends on question_type — see below)
 *   onChange         : (newValue) => void  — not called when readOnly=true
 *   readOnly         : boolean
 *   standardOptions  : object  — severity_level map, used ONLY as fallback for five_option labels
 *                      e.g. { "Not Applicable": 0, "Not Implemented": 1, ... }
 *   itemId           : string  — the subcontrol._id, needed by five_option for DOM spanActive compat
 *   onSpanActive     : (id, tag, index, color) => void  — five_option pill click handler (legacy compat)
 *   index            : number  — position index, passed to onSpanActive
 *
 * Value shape per type:
 *   five_option : string  ("Fully Implemented")
 *   yes_no      : string  ("Yes" | "No" | "N/A")
 *   checkbox    : string[] (["Option A", "Option C"])
 *   radio       : string  ("Option B")
 *   text_box    : string  (free text)
 */

import React from "react";
import {
  Chip,
  FormControl,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";

// ─── Helpers ────────────────────────────────────────────────────────────────

const SEVERITY_COLORS = {
  0: "#333333",
  1: "#B60000",
  2: "#BF9D12",
  3: "#50E232",
  4: "#227C10",
};

const DEFAULT_FIVE_OPTIONS = {
  "Not Applicable": 0,
  "Not Implemented": 1,
  "Partially Implemented": 2,
  "Largely Implemented": 3,
  "Fully Implemented": 4,
};

const YES_NO_COLORS = {
  Yes: "#227C10",
  No: "#B60000",
  "N/A": "#333333",
};

/**
 * Maps a new-type answer back to a legacy severity string.
 * This keeps the existing submit path working while v2 scoring is not yet deployed.
 */
export const mapAnswerToSeverity = (questionType, answerValue) => {
  switch (questionType) {
    case "five_option":
      return answerValue || "Not Applicable";
    case "yes_no":
      if (answerValue === "Yes") return "Fully Implemented";
      if (answerValue === "No") return "Not Implemented";
      return "Not Applicable";
    case "checkbox": {
      // Proportional mapping to nearest five_option label
      const selected = Array.isArray(answerValue) ? answerValue.length : 0;
      if (selected === 0) return "Not Implemented";
      return "Partially Implemented"; // v2 engine will score precisely
    }
    case "radio":
      return answerValue ? "Partially Implemented" : "Not Applicable";
    case "text_box":
      return "Partially Implemented"; // placeholder until reviewer scores
    default:
      return "Not Applicable";
  }
};

// ─── Sub-components ──────────────────────────────────────────────────────────

/**
 * FiveOptionPills — renders the existing pill buttons.
 * Keeps full backward compatibility with spanActive DOM approach.
 */
const FiveOptionPills = ({
  itemId,
  value,
  standardOptions,
  answer_config,
  readOnly,
  onSpanActive,
  index,
}) => {
  // Use answer_config.options if present, else fall back to standardOptions map
  const options = answer_config?.options
    ? answer_config.options.map((o) => ({ label: o.label, value: o.value }))
    : Object.entries(standardOptions || DEFAULT_FIVE_OPTIONS).map(
        ([label, val]) => ({
          label,
          value: val,
        }),
      );

  return (
    <div className="optionsline" id={itemId}>
      {options.map((opt, idx) => {
        const tag = opt.label.trim().toLowerCase();
        const color = SEVERITY_COLORS[opt.value] || "#000000";
        const abbr =
          opt.label.match(/[A-Z]/g)?.slice(0, 2).join("") ||
          opt.label.slice(0, 2).toUpperCase();
        const isActive = value === opt.label;

        return (
          <React.Fragment key={opt.label}>
            <span
              className={`standardoption ${tag}${
                isActive ? " active standardoptionactive" : ""
              }`}
              style={{
                color,
                backgroundColor: isActive ? color : undefined,
                cursor: readOnly ? "default" : "pointer",
                opacity: readOnly && !isActive ? 0.5 : 1,
              }}
              name={opt.label}
              data={itemId}
              onClick={
                readOnly
                  ? undefined
                  : () =>
                      onSpanActive && onSpanActive(itemId, tag, index, color)
              }
            >
              {abbr}
            </span>
            <span className="spacespan" />
          </React.Fragment>
        );
      })}
    </div>
  );
};

/**
 * YesNoButtons — renders Yes / No / N/A as styled buttons.
 */
const YesNoButtons = ({ value, answer_config, readOnly, onChange }) => {
  const labels = answer_config?.labels || { yes: "Yes", no: "No", na: "N/A" };
  const options = [
    { label: labels.yes, key: "Yes" },
    { label: labels.no, key: "No" },
    { label: labels.na, key: "N/A" },
  ];

  return (
    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
      {options.map((opt) => {
        const isSelected = value === opt.key;
        const color = YES_NO_COLORS[opt.key] || "#555";
        return (
          <button
            key={opt.key}
            type="button"
            onClick={readOnly ? undefined : () => onChange && onChange(opt.key)}
            style={{
              padding: "6px 18px",
              borderRadius: "20px",
              border: `2px solid ${color}`,
              backgroundColor: isSelected ? color : "transparent",
              color: isSelected ? "#fff" : color,
              fontWeight: 600,
              fontSize: "13px",
              cursor: readOnly ? "default" : "pointer",
              transition: "all 0.15s",
              opacity: readOnly && !isSelected ? 0.45 : 1,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

/**
 * CheckboxGroup — multi-select options with min/max enforcement.
 */
const CheckboxGroup = ({ value = [], answer_config, readOnly, onChange }) => {
  const options = answer_config?.options || [];
  const maxSelect = answer_config?.maxSelect || options.length;
  const minSelect = answer_config?.minSelect || 1;
  const selected = Array.isArray(value) ? value : [];

  const handleToggle = (opt) => {
    if (readOnly) return;
    let next;
    if (selected.includes(opt)) {
      next = selected.filter((s) => s !== opt);
    } else {
      if (selected.length >= maxSelect) return; // at max, ignore click
      next = [...selected, opt];
    }
    onChange && onChange(next);
  };

  return (
    <div>
      <div style={{ marginBottom: "4px", fontSize: "12px", color: "#666" }}>
        {selected.length}/{maxSelect} selected
        {minSelect > 1 && ` (min ${minSelect})`}
      </div>
      <FormControl component="fieldset">
        {options.map((opt) => {
          const isChecked = selected.includes(opt);
          const atMax = selected.length >= maxSelect && !isChecked;
          return (
            <FormControlLabel
              key={opt}
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={() => handleToggle(opt)}
                  disabled={readOnly || atMax}
                  size="small"
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": { color: "#1976d2" },
                  }}
                />
              }
              label={opt}
              sx={{ opacity: readOnly && !isChecked ? 0.5 : 1 }}
            />
          );
        })}
      </FormControl>
    </div>
  );
};

/**
 * RadioSelect — single-select from ordered list.
 */
const RadioSelect = ({ value, answer_config, readOnly, onChange }) => {
  const options = answer_config?.options || [];

  return (
    <FormControl component="fieldset">
      <RadioGroup
        value={value || ""}
        onChange={(e) => !readOnly && onChange && onChange(e.target.value)}
      >
        {options.map((opt) => (
          <FormControlLabel
            key={opt}
            value={opt}
            control={
              <Radio
                disabled={readOnly}
                size="small"
                sx={{ "&.Mui-checked": { color: "#1976d2" } }}
              />
            }
            label={opt}
            sx={{ opacity: readOnly && value !== opt ? 0.5 : 1 }}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

/**
 * TextBoxInput — free text answer with character counter.
 * In readOnly mode renders as a styled block.
 */
const TextBoxInput = ({ value = "", answer_config, readOnly, onChange }) => {
  const maxLength = answer_config?.maxLength || 2000;
  const placeholder = answer_config?.placeholder || "Type your answer here...";
  const reviewerInstructions = answer_config?.reviewerInstructions;

  if (readOnly) {
    return (
      <div>
        {reviewerInstructions && (
          <div
            style={{
              fontSize: "11px",
              color: "#888",
              marginBottom: "4px",
              fontStyle: "italic",
            }}
          >
            <PersonIcon
              sx={{ fontSize: 12, mr: 0.5, verticalAlign: "middle" }}
            />
            Reviewer instructions: {reviewerInstructions}
          </div>
        )}
        <div
          style={{
            background: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            padding: "8px 12px",
            minHeight: "60px",
            fontSize: "14px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {value || <span style={{ color: "#aaa" }}>{placeholder}</span>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <TextField
        multiline
        minRows={3}
        maxRows={8}
        fullWidth
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        inputProps={{ maxLength }}
        size="small"
        variant="outlined"
        sx={{ fontSize: "14px" }}
      />
      <div
        style={{
          textAlign: "right",
          fontSize: "11px",
          color: value.length >= maxLength ? "#B60000" : "#888",
          marginTop: "2px",
        }}
      >
        {value.length}/{maxLength}
      </div>
    </div>
  );
};

// Add this new component above the main QuestionRenderer

const ReadOnlyAnswer = ({ question_type, value, answer_config }) => {
  // five_option — colored pill with abbreviation
  if (question_type === "five_option") {
    const options = answer_config?.options
      ? answer_config.options
      : Object.entries(DEFAULT_FIVE_OPTIONS).map(([label, val]) => ({
          label,
          value: val,
        }));

    const matched = options.find(
      (o) => o.label === value || o.value === value
    );
    const label = matched?.label || value || "—";
    const score = matched?.value ?? DEFAULT_FIVE_OPTIONS[label];
    const color = SEVERITY_COLORS[score] || "#555";
    const abbr =
      label.match(/[A-Z]/g)?.slice(0, 2).join("") ||
      label.slice(0, 2).toUpperCase();

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: color,
            color: "#fff",
            fontWeight: 700,
            fontSize: "11px",
          }}
        >
          {abbr}
        </span>
        <span style={{ fontSize: "13px", color: "#333" }}>{label}</span>
      </div>
    );
  }

  // yes_no — colored badge
  if (question_type === "yes_no") {
    const color = YES_NO_COLORS[value] || "#555";
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 14px",
          borderRadius: "20px",
          backgroundColor: color,
          color: "#fff",
          fontWeight: 600,
          fontSize: "13px",
        }}
      >
        {value || "—"}
      </span>
    );
  }

  // checkbox — list of selected options as chips
  if (question_type === "checkbox") {
    const selected = Array.isArray(value) ? value : [];
    if (selected.length === 0)
      return <span style={{ color: "#aaa", fontSize: "13px" }}>None selected</span>;
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
        {selected.map((opt) => (
          <Chip
            key={opt}
            label={opt}
            size="small"
            sx={{ backgroundColor: "#1976d2", color: "#fff", fontSize: "12px" }}
          />
        ))}
      </div>
    );
  }

  // radio — single selected option
  if (question_type === "radio") {
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 14px",
          borderRadius: "20px",
          backgroundColor: "#7b1fa2",
          color: "#fff",
          fontWeight: 600,
          fontSize: "13px",
        }}
      >
        {value || "—"}
      </span>
    );
  }

  // text_box — styled text block
  if (question_type === "text_box") {
    const reviewerInstructions = answer_config?.reviewerInstructions;
    return (
      <div>
        {reviewerInstructions && (
          <div
            style={{
              fontSize: "11px",
              color: "#888",
              marginBottom: "4px",
              fontStyle: "italic",
            }}
          >
            <PersonIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: "middle" }} />
            Reviewer note: {reviewerInstructions}
          </div>
        )}
        <div
          style={{
            background: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            padding: "8px 12px",
            minHeight: "40px",
            fontSize: "13px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: value ? "#333" : "#aaa",
          }}
        >
          {value || "No answer provided"}
        </div>
      </div>
    );
  }

  return <span style={{ color: "#aaa" }}>—</span>;
};

// ─── QuestionTypeBadge ────────────────────────────────────────────────────────

export const QUESTION_TYPE_LABELS = {
  five_option: "Five Option",
  yes_no: "Yes / No",
  checkbox: "Checkbox",
  radio: "Radio",
  text_box: "Text Box",
};

const TYPE_CHIP_COLORS = {
  five_option: "#1976d2",
  yes_no: "#2e7d32",
  checkbox: "#f57c00",
  radio: "#7b1fa2",
  text_box: "#c62828",
};

export const QuestionTypeBadge = ({ type }) => (
  <Chip
    label={QUESTION_TYPE_LABELS[type] || type}
    size="small"
    sx={{
      backgroundColor: TYPE_CHIP_COLORS[type] || "#555",
      color: "#fff",
      fontSize: "0.65rem",
      height: "20px",
    }}
  />
);

// ─── Main Component ──────────────────────────────────────────────────────────

const QuestionRenderer = ({
  question_type = "five_option",
  answer_config = null,
  value,
  onChange,
  readOnly = false,
  standardOptions = DEFAULT_FIVE_OPTIONS,
  itemId,
  onSpanActive,
  index = 0,
  informational = false,   // US-CAP-206: top-level prop, not nested in answer_config

}) => {
    // US-CAP-206: informational — answer still required, never scored
  const isInformational = informational === true;

  const InformationalBanner = () => (
    <div style={{
      display:       'flex',
      alignItems:    'center',
      gap:           6,
      padding:       '5px 10px',
      marginBottom:  6,
      borderRadius:  4,
      background:    '#EAF4FB',
      border:        '1px solid #B3D9F5',
      fontSize:      12,
      color:         '#1565C0',
    }}>
      <span style={{ fontSize: 14 }}>ℹ️</span>
      <span>
        <strong>Informational only</strong> — this question is for information gathering
        and does not affect your compliance score. An answer is still required.
      </span>
    </div>
  );


  // ── readOnly: show clean answer display for all types ──
  if (readOnly) {
    return (
      <ReadOnlyAnswer
        question_type={question_type}
        value={value}
        answer_config={answer_config}
      />
    );
  }
  const renderInput = () => {
    switch (question_type) {
      case "five_option":
        return (
          <FiveOptionPills
            itemId={itemId}
            value={value}
            standardOptions={standardOptions}
            answer_config={answer_config}
            readOnly={readOnly}
            onSpanActive={onSpanActive}
            index={index}
          />
        );

      case "yes_no":
        return (
          <YesNoButtons
            value={value}
            answer_config={answer_config}
            readOnly={readOnly}
            onChange={onChange}
          />
        );

      case "checkbox":
        return (
          <CheckboxGroup
            value={value}
            answer_config={answer_config}
            readOnly={readOnly}
            onChange={onChange}
          />
        );

      case "radio":
        return (
          <RadioSelect
            value={value}
            answer_config={answer_config}
            readOnly={readOnly}
            onChange={onChange}
          />
        );

      case "text_box":
        return (
          <TextBoxInput
            value={value}
            answer_config={answer_config}
            readOnly={readOnly}
            onChange={onChange}
          />
        );

      default:
        // Safety fallback — unknown type renders as five_option
        return (
          <FiveOptionPills
            itemId={itemId}
            value={value}
            standardOptions={standardOptions}
            answer_config={null}
            readOnly={readOnly}
            onSpanActive={onSpanActive}
            index={index}
          />
        );
    }
  };
  return (
    <div>
      {isInformational && <InformationalBanner />}
      {renderInput()}
    </div>
  );
};

export default QuestionRenderer;
